import type { RuntimeAppConfig } from './app-config.type';

/**
 * Reads the runtime override for a configuration key from
 * `window.__BF_CONFIG__`. `/config.js` is loaded by `index.html` as a classic
 * (blocking) script, so in the browser the global is populated before any
 * module in the bundle evaluates. Returns `undefined` in non-browser contexts
 * or when the deployment defines no override for the key.
 *
 * @param {keyof RuntimeAppConfig} key - The configuration key to look up
 * @returns {string | undefined} The runtime override, if one is set
 */
function readRuntimeOverride(key: keyof RuntimeAppConfig): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const value = window.__BF_CONFIG__?.[key];
  // Operators hand-edit /config.js: coerce unquoted booleans/numbers
  // (`FLAG: true`, `WIDTH: 20`) to the string semantics env vars guarantee,
  // so `=== 'true'` and `parseInt` call sites behave identically either way.
  return value == null ? undefined : String(value);
}

/**
 * Warns loudly when the runtime configuration script failed to load or parse.
 * `/config.js` always assigns `window.__BF_CONFIG__` — even with every key
 * commented out — so an `undefined` global in a browser means the file is
 * missing, mis-served (e.g. an SPA fallback returned HTML), or contains a
 * syntax error, and the app is silently running on build-time values.
 * Called once from `main.tsx` at boot; no-op outside the browser.
 *
 * @returns {void}
 */
export function warnIfRuntimeConfigMissing(): void {
  if (typeof window === 'undefined' || window.__BF_CONFIG__ !== undefined) {
    return;
  }
  console.warn(
    '[bloomfilter] Runtime configuration (/config.js) did not load — the file is missing, mis-served, or has a ' +
      'syntax error, so this deployment is running on the values baked in at build time. If you meant to configure ' +
      'this deployment, check the browser Network/Console tabs for errors on /config.js and fix the file in place.',
  );
}

/**
 * Resolves a single configuration value: a non-empty runtime override wins,
 * otherwise the build-time value baked in by Vite is used. Empty strings are
 * treated as "unset" so a placeholder left blank in `/config.js` cannot
 * accidentally erase a baked value.
 *
 * @param {string | undefined} runtimeValue - Override from `window.__BF_CONFIG__`, if any
 * @param {string | undefined} buildTimeValue - Value inlined by Vite from `.env` at build time
 * @returns {string | undefined} The effective configuration value
 */
function resolveConfigValue(runtimeValue: string | undefined, buildTimeValue: string): string;
function resolveConfigValue(runtimeValue: string | undefined, buildTimeValue: string | undefined): string | undefined;
function resolveConfigValue(runtimeValue: string | undefined, buildTimeValue: string | undefined): string | undefined {
  return runtimeValue != null && runtimeValue !== '' ? runtimeValue : buildTimeValue;
}

/**
 * The application's effective configuration: deploy-time overrides from
 * `/config.js` (`window.__BF_CONFIG__`) layered over the build-time `.env`
 * values. ALL reads of environment-dependent settings must go through this
 * object — never `import.meta.env` directly — so a single build artifact can
 * be rehosted against any environment by editing `/config.js` alone.
 *
 * Properties are lazy getters rather than an eval-time snapshot, for two
 * reasons: production behavior is identical (`/config.js` runs before the
 * bundle and is never mutated afterwards), and unit tests keep the
 * established `vi.stubEnv(...)` idiom working — a snapshot would freeze the
 * fallbacks before a test could stub them.
 *
 * Each `import.meta.env.*` reference below is intentionally written out
 * literally: Vite replaces them statically at build time, and dynamic access
 * (`import.meta.env[key]`) would resolve to `undefined` in the bundle.
 */
export const appConfig: RuntimeAppConfig = Object.freeze({
  get VITE_APP_API_URL(): string {
    return resolveConfigValue(readRuntimeOverride('VITE_APP_API_URL'), import.meta.env.VITE_APP_API_URL);
  },
  get VITE_APP_PUBLIC_URL(): string {
    return resolveConfigValue(readRuntimeOverride('VITE_APP_PUBLIC_URL'), import.meta.env.VITE_APP_PUBLIC_URL);
  },
  get VITE_APP_SENTRY_DSN(): string {
    return resolveConfigValue(readRuntimeOverride('VITE_APP_SENTRY_DSN'), import.meta.env.VITE_APP_SENTRY_DSN);
  },
  get VITE_APP_ENV_NAME(): string {
    return resolveConfigValue(readRuntimeOverride('VITE_APP_ENV_NAME'), import.meta.env.VITE_APP_ENV_NAME);
  },
  get VITE_APP_PROCESS_MAP_MAX_EDGE_STROKE_WIDTH(): string {
    return resolveConfigValue(
      readRuntimeOverride('VITE_APP_PROCESS_MAP_MAX_EDGE_STROKE_WIDTH'),
      import.meta.env.VITE_APP_PROCESS_MAP_MAX_EDGE_STROKE_WIDTH,
    );
  },
  get VITE_APP_OTP_RESEND_TIMEOUT(): string {
    return resolveConfigValue(
      readRuntimeOverride('VITE_APP_OTP_RESEND_TIMEOUT'),
      import.meta.env.VITE_APP_OTP_RESEND_TIMEOUT,
    );
  },
  get VITE_APP_ENABLE_SIGN_UP(): string {
    return resolveConfigValue(readRuntimeOverride('VITE_APP_ENABLE_SIGN_UP'), import.meta.env.VITE_APP_ENABLE_SIGN_UP);
  },
  get VITE_APP_HELP_URL(): string {
    return resolveConfigValue(readRuntimeOverride('VITE_APP_HELP_URL'), import.meta.env.VITE_APP_HELP_URL);
  },
  get VITE_APP_GOOGLE_API_KEY(): string {
    return resolveConfigValue(readRuntimeOverride('VITE_APP_GOOGLE_API_KEY'), import.meta.env.VITE_APP_GOOGLE_API_KEY);
  },
  get VITE_GLOBAL_STATE_LOGGING(): string {
    return resolveConfigValue(
      readRuntimeOverride('VITE_GLOBAL_STATE_LOGGING'),
      import.meta.env.VITE_GLOBAL_STATE_LOGGING,
    );
  },
  get VITE_FEATURE_FLAG_USE_ASYNC_CALLS(): string {
    return resolveConfigValue(
      readRuntimeOverride('VITE_FEATURE_FLAG_USE_ASYNC_CALLS'),
      import.meta.env.VITE_FEATURE_FLAG_USE_ASYNC_CALLS,
    );
  },
  get VITE_FEATURE_FLAG_FLORA_MODULE(): string {
    return resolveConfigValue(
      readRuntimeOverride('VITE_FEATURE_FLAG_FLORA_MODULE'),
      import.meta.env.VITE_FEATURE_FLAG_FLORA_MODULE,
    );
  },
  get REACT_QUERY_RETRY_TIMES(): string | undefined {
    return resolveConfigValue(
      readRuntimeOverride('REACT_QUERY_RETRY_TIMES'),
      import.meta.env.REACT_QUERY_RETRY_TIMES as string | undefined,
    );
  },
});
